import React, { useMemo, useState } from 'react';
import {
  ExpressCheckoutElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import {
  CreateSubscriptionMethodMutation,
  useAttachPaymentMethodMutation,
  useCreateSubscriptionMethodMutation,
  useGetClientSecretMutation,
} from 'graphql/generated/hasura';
import {
  StripeExpressCheckoutElementConfirmEvent,
  StripeExpressCheckoutElementOptions,
  StripeExpressCheckoutElementReadyEvent,
} from '@stripe/stripe-js';
import moment from 'moment';
import { capitalize } from 'lodash';
import { useAuth } from 'auth/context/AuthContext';
import { useApolloClient } from '@apollo/client';

const WalletPayment: React.FC<{ callback?: (date?: string) => void }> = ({
  callback = () => ({}),
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const { stripeSubscription } = useAuth();
  const [errorMessage, setErrorMessage] = useState('');
  const [isWalletAvailable, setIsWalletAvailable] = useState(true);
  const client = useApolloClient();

  const [createSubscribe] = useCreateSubscriptionMethodMutation({
    refetchQueries: ['GetPaymentMethodList'],
  });
  const [createProviderSetupIntent] = useGetClientSecretMutation();

  const [attachPaymentMethod] = useAttachPaymentMethodMutation({
    refetchQueries: ['GetPaymentMethodList'],
    onCompleted: () => setErrorMessage(''),
    onError: (error: Error) => setErrorMessage(error.message),
  });

  const serverRequest = (name: string, id: string) => {
    const hasCurrentSubscriptionEnd = moment().isSameOrAfter(
      stripeSubscription?.[0]?.current_period_end,
    );
    return hasCurrentSubscriptionEnd
      ? createSubscribe({
          variables: {
            paymentMethodName: `${capitalize(name)} Card`,
            stripePaymentMethodId: id,
          },
        })
      : attachPaymentMethod({
          variables: {
            paymentMethodName: `${capitalize(name)} Card`,
            stripePaymentMethodId: id,
          },
        });
  };

  const expressCheckoutOptions: StripeExpressCheckoutElementOptions =
    useMemo(() => {
      return {
        buttonTheme: {
          googlePay: 'black',
          paypal: 'blue',
          applePay: 'black',
        },
        paymentMethods: {
          googlePay: 'always',
          applePay: 'auto',
          link: 'never',
        },
      };
    }, []);

  const handleReady = (event: StripeExpressCheckoutElementReadyEvent) => {
    if (
      !event.availablePaymentMethods ||
      Object.keys(event.availablePaymentMethods).length === 0
    ) {
      console.warn('Pay (Apple/Google Pay) not available.');
      setIsWalletAvailable(false);
    }
  };

  const onConfirm = async (event: StripeExpressCheckoutElementConfirmEvent) => {
    if (!stripe || !elements) return;

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setErrorMessage(submitError.message || 'Validate error');
        event.paymentFailed({ reason: 'invalid_payment_data' });
        return;
      }

      const { data, errors } = await createProviderSetupIntent();

      if (errors?.[0].message || !data) {
        setErrorMessage(errors?.[0].message || 'Not clientSecret');
      }

      const result = await stripe.confirmSetup({
        elements,
        clientSecret: data?.createProviderSetupIntent?.clientSecret as string,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });

      if (result.error) {
        setErrorMessage(result.error.message || 'Error by pay setup');
        event.paymentFailed({ reason: 'fail' });
        return;
      }

      const setupIntent = result?.setupIntent;
      if (setupIntent && setupIntent.status === 'succeeded') {
        const paymentMethodId = setupIntent.payment_method as string;

        const { data, errors } = await serverRequest(
          event.expressPaymentType,
          paymentMethodId,
        );
        callback(
          moment(
            (data as CreateSubscriptionMethodMutation)?.createSubscription
              ?.currentPeriodEnd,
          ).format('DD.MM.YYYY'),
        );

        if (errors) {
          setErrorMessage(errors[0].message);
          event.paymentFailed({ reason: 'fail' });
        } else {
          console.log('Subscribe success!', data);
        }
      } else {
        setErrorMessage(`Error: ${setupIntent?.status}`);
        event.paymentFailed({ reason: 'fail' });
      }
    } catch (err) {
      console.error(err);
      setErrorMessage('Unknown error');
      event.paymentFailed({ reason: 'fail' });
    } finally {
      client.refetchQueries({
        include: ['GetStripeSubscriptions'],
      });
    }
  };

  if (!isWalletAvailable) {
    return null;
  }

  return (
    <>
      <div className="flex flex-row w-full items-center my-3 gap-5">
        <hr className="w-full h-px bg-gray-blur" />
        <p className="text-base text-med-gray font-medium">{'or'}</p>
        <hr className="w-full h-px bg-gray-blur" />
      </div>
      <ExpressCheckoutElement
        onConfirm={onConfirm}
        onReady={handleReady}
        options={expressCheckoutOptions}
      />

      {errorMessage && (
        <div className="text-clc-red mt-2.5">{errorMessage}</div>
      )}
    </>
  );
};

export default WalletPayment;
